import type { RecordFolderAssignment } from '@provision/entity-types';
import { useMemo } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { Macro, RecordFolder } from 'src/graphql';
import {
  FAVORITES_ID,
  getFolderEntryId,
  getMacroEntryId,
  UNCATEGORIZED_ID,
} from 'src/pages/Conversations/Chat/Macros/macros-modal/constants';
import {
  GroupedFolders,
  MacroEntry,
  VisibleListEntry,
} from 'src/pages/Conversations/Chat/Macros/macros-modal/types';

interface UseMacroGroupingInput {
  macros: Macro[];
  favoriteMacroIds: string[];
  recordFolders: RecordFolder[];
  recordFolderAssignments: RecordFolderAssignment[];
  partnerIds?: number[];
  expandedFolderIds: Set<string>;
  collapsedSearchFolderIds: Set<string>;
  isSearching: boolean;
  searchValue: string;
  isManageMode: boolean;
}

export const useMacroGrouping = ({
  macros,
  favoriteMacroIds,
  recordFolders,
  recordFolderAssignments,
  partnerIds,
  expandedFolderIds,
  collapsedSearchFolderIds,
  isSearching,
  searchValue,
  isManageMode,
}: UseMacroGroupingInput) => {
  const orderedFolders = useMemo(() => {
    return [...recordFolders].sort((a, b) => {
      const rankA = a.partnerId === PLATFORM_PARTNER_ID ? 0 : 1;
      const rankB = b.partnerId === PLATFORM_PARTNER_ID ? 0 : 1;
      if (rankA !== rankB) {
        return rankA - rankB;
      }
      return a.name.localeCompare(b.name);
    });
  }, [recordFolders]);

  const folderById = useMemo(() => {
    return new Map(orderedFolders.map((folder) => [folder.id, folder]));
  }, [orderedFolders]);

  const scopedAssignments = useMemo(() => {
    if (!partnerIds?.length) {
      return recordFolderAssignments;
    }

    return recordFolderAssignments.filter((assignment) =>
      partnerIds.includes(assignment.partnerId),
    );
  }, [partnerIds, recordFolderAssignments]);

  const macroEntries = useMemo<MacroEntry[]>(() => {
    return macros.map((macro) => {
      const assignment = scopedAssignments.find(
        (entry) => entry.entityId === macro.id && entry.entity === 'Macro',
      );
      const folder = assignment ? (folderById.get(assignment.recordFolderId) ?? null) : null;

      return { macro, folder };
    });
  }, [folderById, macros, scopedAssignments]);

  const filteredEntries = useMemo(() => {
    if (!isSearching) {
      return macroEntries;
    }

    return macroEntries.filter(({ macro, folder }) => {
      const folderName = folder?.name ?? 'Uncategorized';
      const content = `${macro.name} ${macro.description || ''} ${folderName}`
        .toLowerCase()
        .trim();
      return content.includes(searchValue);
    });
  }, [isSearching, macroEntries, searchValue]);

  const groupedFolders = useMemo<GroupedFolders>(() => {
    const byFolderId = new Map<string, Macro[]>();
    const uncategorized: Macro[] = [];

    filteredEntries.forEach(({ macro, folder }) => {
      if (folder) {
        const existing = byFolderId.get(folder.id) ?? [];
        byFolderId.set(folder.id, [...existing, macro]);
        return;
      }

      uncategorized.push(macro);
    });

    const folderSections = orderedFolders
      .map((folder) => ({
        id: folder.id,
        name: folder.name,
        folder,
        macros: byFolderId.get(folder.id) ?? [],
      }))
      .filter((section) => {
        if (isSearching) {
          const folderMatchesSearch = section.name.toLowerCase().includes(searchValue);
          if (!folderMatchesSearch && section.macros.length === 0) {
            return false;
          }
        }

        // Hide empty platform folders when not in manage mode
        if (!isManageMode) {
          const isPlatformFolder = section.folder.partnerId === PLATFORM_PARTNER_ID;
          if (isPlatformFolder && section.macros.length === 0) {
            return false;
          }
        }

        return true;
      });

    const uncategorizedSection = {
      id: 'uncategorized',
      name: 'Uncategorized',
      folder: null,
      macros: uncategorized,
    };

    const favoritesSet = new Set(favoriteMacroIds);
    const favoriteMacros = filteredEntries
      .map((entry) => entry.macro)
      .filter((macro) => favoritesSet.has(macro.id))
      .sort((a, b) => a.name.localeCompare(b.name));

    return {
      favoritesSection: favoriteMacros.length
        ? {
            id: 'favorites',
            name: 'Favorites',
            macros: favoriteMacros,
          }
        : null,
      folderSections,
      uncategorizedSection: uncategorized.length ? uncategorizedSection : null,
    };
  }, [
    favoriteMacroIds,
    filteredEntries,
    isManageMode,
    isSearching,
    orderedFolders,
    searchValue,
  ]);

  const expandedBySearch = useMemo(() => {
    if (!isSearching) {
      return new Set<string>();
    }

    return new Set(groupedFolders.folderSections.map((section) => section.id));
  }, [groupedFolders, isSearching]);

  const effectiveExpandedFolders = useMemo(() => {
    if (!isSearching) {
      return expandedFolderIds;
    }

    const expanded = new Set(expandedBySearch);
    collapsedSearchFolderIds.forEach((folderId) => expanded.delete(folderId));
    return expanded;
  }, [collapsedSearchFolderIds, expandedBySearch, expandedFolderIds, isSearching]);

  const visibleListEntries = useMemo<VisibleListEntry[]>(() => {
    const entries: VisibleListEntry[] = [];

    if (groupedFolders.favoritesSection) {
      entries.push({
        type: 'folder',
        id: getFolderEntryId(FAVORITES_ID),
        folderId: FAVORITES_ID,
        name: groupedFolders.favoritesSection.name,
      });
      groupedFolders.favoritesSection.macros.forEach((macro) => {
        entries.push({
          type: 'macro',
          id: getMacroEntryId(macro.id, FAVORITES_ID),
          macro,
          folderId: FAVORITES_ID,
        });
      });
    }

    groupedFolders.folderSections.forEach((section) => {
      entries.push({
        type: 'folder',
        id: getFolderEntryId(section.id),
        folderId: section.id,
        name: section.name,
      });

      if (effectiveExpandedFolders.has(section.id)) {
        section.macros.forEach((macro) => {
          entries.push({
            type: 'macro',
            id: getMacroEntryId(macro.id, section.id),
            macro,
            folderId: section.id,
          });
        });
      }
    });

    if (groupedFolders.uncategorizedSection) {
      entries.push({
        type: 'folder',
        id: getFolderEntryId(UNCATEGORIZED_ID),
        folderId: null,
        name: groupedFolders.uncategorizedSection.name,
      });

      groupedFolders.uncategorizedSection.macros.forEach((macro) => {
        entries.push({
          type: 'macro',
          id: getMacroEntryId(macro.id, UNCATEGORIZED_ID),
          macro,
          folderId: null,
        });
      });
    }

    return entries;
  }, [effectiveExpandedFolders, groupedFolders]);

  const visibleEntryIndexById = useMemo(() => {
    const indexMap = new Map<string, number>();
    visibleListEntries.forEach((entry, index) => {
      indexMap.set(entry.id, index);
    });
    return indexMap;
  }, [visibleListEntries]);

  return {
    filteredEntries,
    groupedFolders,
    effectiveExpandedFolders,
    visibleListEntries,
    visibleEntryIndexById,
  };
};
